import { defineStore } from 'pinia';
import { AxiosRequestConfig } from "axios";
import getCsrfTokenFromCookie from "@/util/csrf-token";
import handleTeacherError from "@/util/handle_teacher_error";
import apiRequest from '@/util/api-request';
import { IStudents } from "@/consts/interfaces";

interface AcademicSession {
  year: number;
}

interface Classroom {
  name: string;
  academic_session: AcademicSession;
}

interface ImportEditResult {
  status: "変更" | "削除" | null;
  number: string;
  name: string;
}

interface ImportEditResultsBase {
  fileName: string;
}

interface ImportEditResultsSuccess extends ImportEditResultsBase {
  type: "success";
  /** 成功（）の `（）` の中に入る文言（例: `1名を変更しました／2名を削除しました`） */
  message: string;
  results: ImportEditResult[];
}

interface ImportEditResultsFailure extends ImportEditResultsBase {
  type: "failure";
  /** 「登録結果」の下に表示される赤字のメッセージ */
  message: string;
  /** 「エラー内容」の表に列挙するエラーメッセージ */
  errors: string[];
}

const useClassroomStore = defineStore("teacherClassroomStore", {
  actions: {
    async retrieveClassroom({ classroom_uuid, }: { classroom_uuid: string }): Promise<Classroom> {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: `/teacher/api/school/classrooms/${classroom_uuid}/`,
          headers: {
            "Content-Type": "application/json",
          },
        };
        const response = await apiRequest(config);
        return response.data
      } catch (e) {
        handleTeacherError(e)
      }
    },

    async fetchStudentList({ classroom_uuid, }: { classroom_uuid: string }): Promise<IStudents> {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          url: `/teacher/api/school/classrooms/${classroom_uuid}/students/`,
          headers: {
            "Content-Type": "application/json",
          }
        };
        const response = await apiRequest(config);
        return response.data
      } catch (e) {
        handleTeacherError(e)
      }
    },

    async downloadStudentsImportEditExcel({ classroom_uuid, }: { classroom_uuid: string }) {
      try {
        const config: AxiosRequestConfig = {
          method: "get",
          responseType: "blob",
          url: `/teacher/api/classrooms/${classroom_uuid}/students/import_edit/`,
          headers: {
            "Content-Type": "application/json",
          },
        };
        const result = await apiRequest(config);
        return result
      } catch (e) {
        // fetchDownloadStudentsRoster を参考に実装した.
        // ↓明示的に Json Response する status コードをここに追加する
        if ([400, 404].includes(e.response.status)) {
          e = await new Promise((resolve) => {
            const fileReader = new FileReader();
            fileReader.onload = () => {
              if (typeof fileReader.result === "string") {
                e.response.data = JSON.parse(fileReader.result);
              }
              resolve(e);
            };
            fileReader.readAsText(e.response.data);
          });
        }
        handleTeacherError(e)
      }
    },

    async importEditStudents({classroom_uuid, payload}) {
      try {
        const config: AxiosRequestConfig = {
          method: "post",
          url: `/teacher/api/classrooms/${classroom_uuid}/students/import_edit/`,
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRFToken": getCsrfTokenFromCookie(document.cookie)
          },
          data: payload
        };
        const results = await apiRequest(config);
        return results.data
      } catch (e) {
        handleTeacherError(e)
      }
    }
  }
})

export {
  useClassroomStore,
  Classroom,
  ImportEditResultsSuccess,
  ImportEditResultsFailure,
}
